import { Minus } from "@phosphor-icons/react";
import { Plus } from "@phosphor-icons/react/dist/ssr";
import { FC, useState } from "react";

export interface PassengerCounts {
  adults: number;
  children: number;
  infants: number;
}

interface PassengerSelectorProps {
  /** Pass value + onChange to make this a controlled input; omit both to
   * keep the original uncontrolled/self-contained behaviour. */
  value?: PassengerCounts;
  onChange?: (value: PassengerCounts) => void;
}

const PassengerSelector: FC<PassengerSelectorProps> = ({ value, onChange }) => {
  const isControlled = value !== undefined;

  const [internalCounts, setInternalCounts] = useState<PassengerCounts>({
    adults: 1,
    children: 0,
    infants: 0,
  });

  const counts = isControlled ? value : internalCounts;

  const update = (next: Partial<PassengerCounts>) => {
    const merged = { ...counts, ...next };
    if (!isControlled) {
      setInternalCounts(merged);
    }
    onChange?.(merged);
  };

  return (
    <div>
      <h2 className="text-lg font-semibold mb-4">Select Passengers</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 border rounded-lg hover:bg-gray-100 cursor-pointer flex items-center justify-between">
          <div>
            <p className="font-medium">
              {counts.adults} Adult{counts.adults > 1 ? "s" : ""}
            </p>
            <p className="text-sm text-gray-600">Aged 12 and above</p>
          </div>
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => update({ adults: Math.max(counts.adults - 1, 1) })}
              className="p-1 rounded-full border border-gray-300 hover:bg-gray-200 text-visaro-orange"
            >
              <Minus />
            </button>
            <span>{counts.adults}</span>
            <button
              type="button"
              onClick={() => update({ adults: counts.adults + 1 })}
              className="p-1 rounded-full border border-gray-300 hover:bg-gray-200 text-visaro-orange"
            >
              <Plus />
            </button>
          </div>
        </div>
        <div className="p-4 border rounded-lg hover:bg-gray-100 cursor-pointer flex items-center justify-between">
          <div>
            <p className="font-medium">
              {counts.children} Child{counts.children > 1 ? "ren" : ""}
            </p>
            <p className="text-sm text-gray-600">Aged 2 to 11</p>
          </div>
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => update({ children: Math.max(counts.children - 1, 0) })}
              className="p-1 rounded-full border border-gray-300 hover:bg-gray-200 text-visaro-orange"
            >
              <Minus />
            </button>
            <span>{counts.children}</span>
            <button
              type="button"
              onClick={() => update({ children: counts.children + 1 })}
              className="p-1 rounded-full border border-gray-300 hover:bg-gray-200 text-visaro-orange"
            >
              <Plus />
            </button>
          </div>
        </div>
        <div className="p-4 border rounded-lg hover:bg-gray-100 cursor-pointer flex items-center justify-between">
          <div>
            <p className="font-medium">
              {counts.infants} Infant{counts.infants > 1 ? "s" : ""}
            </p>
            <p className="text-sm text-gray-600">Aged below 2</p>
          </div>
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => update({ infants: Math.max(counts.infants - 1, 0) })}
              className="p-1 rounded-full border border-gray-300 hover:bg-gray-200 text-visaro-orange"
            >
              <Minus />
            </button>
            <span>{counts.infants}</span>
            <button
              type="button"
              onClick={() => update({ infants: counts.infants + 1 })}
              className="p-1 rounded-full border border-gray-300 hover:bg-gray-200 text-visaro-orange"
            >
              <Plus />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PassengerSelector;
